import { useHotkeys } from "@mantine/hooks";
import { Button } from "@win-reality/react-ui";
import { useDispatch, useSelector } from "react-redux";

import {
  incrementTaggingFlowStep,
  setTaggingFlowStep,
} from "../../../../store/slices/hotkeys";
import {
  putTag,
  removeTag,
  setTaggingState,
} from "../../../../store/slices/session";
import type { RootState } from "../../../../store/store";

/**
 * SoftballPitchTypeStep - Select softball pitch type
 *
 * Options:
 * - Fastball (F), Screwball (S)
 * - Dropball (D), Changeup (C)
 * - Riseball (R), LowRise (L)
 * - Curveball (V), DropCurve (P)
 */
export const SoftballPitchTypeStep = () => {
  const tagData = useSelector((state: RootState) => state.session.tagData);
  const dispatch = useDispatch();
  const tagKey = "pitchType";

  function handleTag(value: string) {
    dispatch(setTaggingState(true));
    if (tagData[tagKey] === value) {
      dispatch(removeTag({ key: tagKey }));
    } else {
      dispatch(putTag({ key: tagKey, value }));
      dispatch(incrementTaggingFlowStep());
    }
  }

  const hotkeys = {
    Fastball: "F",
    Dropball: "D",
    Riseball: "R",
    Curveball: "V",
    Screwball: "S",
    LowRise: "L",
    Changeup: "C",
    DropCurve: "P",
  };

  useHotkeys([
    [hotkeys.Fastball, () => handleTag("Fastball")],
    [hotkeys.Dropball, () => handleTag("Dropball")],
    [hotkeys.Riseball, () => handleTag("Riseball")],
    [hotkeys.Curveball, () => handleTag("Curveball")],
    [hotkeys.Screwball, () => handleTag("Screwball")],
    [hotkeys.LowRise, () => handleTag("LowRise")],
    [hotkeys.Changeup, () => handleTag("Changeup")],
    [hotkeys.DropCurve, () => handleTag("DropCurve")],
  ]);

  return (
    <div className="flex w-full flex-col items-stretch gap-0">
      {/* Previously completed tag - clickable to jump back */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className="flex h-10 cursor-pointer items-center justify-center"
        onClick={() => dispatch(setTaggingFlowStep(0))}
      >
        <p className="text-sm font-bold text-blue">
          {tagData.prePitchStance ?? "---"}
        </p>
      </div>

      {/* Instructions */}
      <div className="flex h-10 items-center justify-center">
        <p className="text-sm italic text-text-light">Select Pitch Type</p>
      </div>

      {/* Button Grid - 2 columns */}
      <div className="flex w-full gap-0">
        {/* Left Column */}
        <div className="flex flex-1 flex-col gap-0">
          <div className="flex h-10 items-center justify-center gap-2 px-2">
            <Button
              variant={tagData[tagKey] === "Fastball" ? "blue" : "ghost"}
              onClick={() => handleTag("Fastball")}
              size="sm"
              className="flex-1 normal-case border border-gray-300"
            >
              <span className="flex items-center justify-between w-full">
                Fastball
                <kbd className="ml-2 rounded bg-[hsla(218,100%,52%,0.1)] px-2 py-1 text-xs font-semibold">
                  {hotkeys.Fastball}
                </kbd>
              </span>
            </Button>
          </div>

          <div className="flex h-10 items-center justify-center gap-2 px-2">
            <Button
              variant={tagData[tagKey] === "Dropball" ? "blue" : "ghost"}
              onClick={() => handleTag("Dropball")}
              size="sm"
              className="flex-1 normal-case border border-gray-300"
            >
              <span className="flex items-center justify-between w-full">
                Dropball
                <kbd className="ml-2 rounded bg-[hsla(218,100%,52%,0.1)] px-2 py-1 text-xs font-semibold">
                  {hotkeys.Dropball}
                </kbd>
              </span>
            </Button>
          </div>

          <div className="flex h-10 items-center justify-center gap-2 px-2">
            <Button
              variant={tagData[tagKey] === "Riseball" ? "blue" : "ghost"}
              onClick={() => handleTag("Riseball")}
              size="sm"
              className="flex-1 normal-case border border-gray-300"
            >
              <span className="flex items-center justify-between w-full">
                Riseball
                <kbd className="ml-2 rounded bg-[hsla(218,100%,52%,0.1)] px-2 py-1 text-xs font-semibold">
                  {hotkeys.Riseball}
                </kbd>
              </span>
            </Button>
          </div>

          <div className="flex h-10 items-center justify-center gap-2 px-2">
            <Button
              variant={tagData[tagKey] === "Curveball" ? "blue" : "ghost"}
              onClick={() => handleTag("Curveball")}
              size="sm"
              className="flex-1 normal-case border border-gray-300"
            >
              <span className="flex items-center justify-between w-full">
                Curveball
                <kbd className="ml-2 rounded bg-[hsla(218,100%,52%,0.1)] px-2 py-1 text-xs font-semibold">
                  {hotkeys.Curveball}
                </kbd>
              </span>
            </Button>
          </div>
        </div>

        {/* Right Column */}
        <div className="flex flex-1 flex-col gap-0">
          <div className="flex h-10 items-center justify-center gap-2 px-2">
            <Button
              variant={tagData[tagKey] === "Screwball" ? "blue" : "ghost"}
              onClick={() => handleTag("Screwball")}
              size="sm"
              className="flex-1 normal-case border border-gray-300"
            >
              <span className="flex items-center justify-between w-full">
                Screwball
                <kbd className="ml-2 rounded bg-[hsla(218,100%,52%,0.1)] px-2 py-1 text-xs font-semibold">
                  {hotkeys.Screwball}
                </kbd>
              </span>
            </Button>
          </div>

          <div className="flex h-10 items-center justify-center gap-2 px-2">
            <Button
              variant={tagData[tagKey] === "Changeup" ? "blue" : "ghost"}
              onClick={() => handleTag("Changeup")}
              size="sm"
              className="flex-1 normal-case border border-gray-300"
            >
              <span className="flex items-center justify-between w-full">
                Changeup
                <kbd className="ml-2 rounded bg-[hsla(218,100%,52%,0.1)] px-2 py-1 text-xs font-semibold">
                  {hotkeys.Changeup}
                </kbd>
              </span>
            </Button>
          </div>

          <div className="flex h-10 items-center justify-center gap-2 px-2">
            <Button
              variant={tagData[tagKey] === "LowRise" ? "blue" : "ghost"}
              onClick={() => handleTag("LowRise")}
              size="sm"
              className="flex-1 normal-case border border-gray-300"
            >
              <span className="flex items-center justify-between w-full">
                LowRise
                <kbd className="ml-2 rounded bg-[hsla(218,100%,52%,0.1)] px-2 py-1 text-xs font-semibold">
                  {hotkeys.LowRise}
                </kbd>
              </span>
            </Button>
          </div>

          <div className="flex h-10 items-center justify-center gap-2 px-2">
            <Button
              variant={tagData[tagKey] === "DropCurve" ? "blue" : "ghost"}
              onClick={() => handleTag("DropCurve")}
              size="sm"
              className="flex-1 normal-case border border-gray-300"
            >
              <span className="flex items-center justify-between w-full">
                DropCurve
                <kbd className="ml-2 rounded bg-[hsla(218,100%,52%,0.1)] px-2 py-1 text-xs font-semibold">
                  {hotkeys.DropCurve}
                </kbd>
              </span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
